<script>
/* Imports */
import {
  mapActions,
  mapGetters,
} from 'vuex';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getCategory3Data,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Helpers */
import {
  getProductPageUrl,
  announceAccessibilityMessage,
} from '../helpers/mainHelpers.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';
import { useHandleAffirmUpdate } from '../composables/useHandleAffirmUpdate.js';

export default {
  name: 'ColorPicker',
  setup() {
    /* Initialize Composables */
    const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
    const { handleAffirmUpdate } = useHandleAffirmUpdate();
    return {
      genericAnalyticsData,
      handleAffirmUpdate,
    };
  },
  data() {
    return {
      preloadedSkus: [],
    };
  },
  computed: {
    ...mapGetters([
      'getBootstrap',
      'getOptionListByType',
      'getAllOptionItems',
      'getGlobalProductName',
      'getOptionsList',
      'getProductBySku',
      'getPartNumber',
    ]),
    optionsList() {
      return this.getOptionListByType('image');
    },
  },
  methods: {
    ...mapActions(['updateProductData', 'setProductActiveTabId']),
    getImages(imageCategory) {
      return this.getAllOptionItems(imageCategory);
    },
    getImageAltText(item) {
      return this.getBootstrap?.skus?.[item?.sku]?.pvName ?? item?.altText ?? null;
    },
    getSelectedOptionText(option) {
      const selectedOption = this.getImages(option.id)?.find((image) => image.selected) || {};
      if (!selectedOption) return '';
      const productDetails = this.getProductBySku(selectedOption?.sku);
      return productDetails?.productVariation ? `<span data-testid="color-picker-title-selected-option-text" style="font-weight: 400;" > - ${productDetails.productVariation}</span>` : '';
    },
    getColorPickerTitle(option) {
      const selectedText = this.getSelectedOptionText(option);
      return `<span data-testid="color-picker-title" style="font-weight: 700;" >${option?.title}</span>${selectedText}`;
    },
    calculateLinkClass(image) {
      return {
        app__color__picker__link__active: image.enabled,
        js__active: image.enabled,
        app__color__picker__link__selected: image.selected,
        js__selected: image.selected,
      };
    },
    getUrl(url) {
      return url ? getProductPageUrl(url, window?.location?.origin) : '';
    },
    getAnalyticsProductName() {
      return sanitizeAnalyticsString(this.getGlobalProductName) || 'NA';
    },
    getCategory3Value() {
      const productName = this.getAnalyticsProductName();
      return getCategory3Data(productName);
    },
    sendColorPickerAnalyticsEvent(item) {
      if (!item) return;

      const selectedProduct = this.getProductBySku(item?.sku);

      const productPrice = {
        priceObj: {
          listPrice: selectedProduct?.price?.listPrice,
          salePrice: selectedProduct?.price?.salePrice,
        },
        sellable: selectedProduct?.isSellable,
        showPrice: selectedProduct?.showPrice,
      };

      const selectedProductName = sanitizeAnalyticsString(selectedProduct?.globalProductName);
      const selectedProductIndex = this.getOptionListByType('image')[0]?.options?.findIndex((option) => option.sku === item.sku);
      const productCategory4 = this.getOptionListByType('ems')[0]?.options?.find((option) => option.sku === item.sku)?.text || 'NA';
      const productCategory5 = this.getOptionListByType('image')[0]?.options?.find((option) => option.sku === item.sku)?.text || 'NA';

      const analyticsData = {
        ...this.genericAnalyticsData(),
        // Send the following fields as 'NA' for now since they are not available in the current context
        creative_name: 'NA',
        creative_slot: 'NA',
        location_id: 'NA',
        promotion_name: 'NA',
        promotion_id: 'NA',
        bundle_id: 'NA',
        is_bundle: 'NA',
        sale_type: 'NA',
        item_list_id: ['Product Page EMS'],
        item_list_name: [`${selectedProductName} - Product Page EMS - Thumbnail`],
        index: [selectedProductIndex + 1],
        product_id: selectedProduct?.productId ? [selectedProduct?.productId] : ['NA'],
        product_brand: window?.utag_data?.product_brand || ['NA'],
        product_category: window?.utag_data?.product_category || ['NA'],
        product_category2: window?.utag_data?.product_category2 || ['NA'],
        product_category3: this.getCategory3Value(),
        product_category4: [productCategory4],
        product_category5: [productCategory5],
        product_variant: selectedProduct?.productVariation ? [sanitizeAnalyticsString(selectedProduct?.productVariation)] : ['NA'],
        product_name: selectedProduct?.globalProductName ? [selectedProductName] : ['NA'],
        product_original_price: [getAnalyticsPriceData(productPrice).listPrice],
        product_price: [getAnalyticsPriceData(productPrice).price],
        product_sku: item?.sku ? [item.sku] : ['NA'],
        product_url: [item?.url || 'NA'],
        product_availability: [getAnalyticsPriceData(productPrice).availability],
        item_family: this.getCategory3Value(),
        is_sale: getAnalyticsPriceData(productPrice).isSale,
        in_stock: getAnalyticsPriceData(productPrice).inStock,
        price_shown: getAnalyticsPriceData(productPrice).priceShown,
        link_type: ['EMS image'],
        tealium_event: 'select_item',
      };

      sendAnalyticsEvent(analyticsData);
    },
    async changeColor(item) {
      const altText = this.getImageAltText(item);

      if (altText) {
        // Accessibility announcement
        announceAccessibilityMessage(altText);
      }

      // If we are already displaying this sku, don't update the product data
      if (item.sku === this.getPartNumber) {
        return;
      }

      // Send analytics event
      this.sendColorPickerAnalyticsEvent(item);

      // Retrieve the updated product data from Store
      const selectedProduct = this.getProductBySku(item.sku);

      // Update Affirm promotions with the new price
      await this.handleAffirmUpdate(selectedProduct);

      // Update the product data
      await this.updateProductData(item.sku);
    },
    preloadImages(item) {
      // If we already preloaded it or is the current product do nothing
      if (this.preloadedSkus[item.sku] || this.getPartNumber === item.sku) return;

      const productDetails = this.getProductBySku(item.sku);

      // In case the SKU is not available simply return
      if (!productDetails) return;

      // We want the default image and the rest of the gallery
      const images = [{ ...productDetails.images.defaultImage }, ...(productDetails.images.gallery || [])];

      // Do the actual preloading
      images.forEach((imgObj) => {
        const imgSrc = imgObj.image || imgObj.imageSrc;
        if (!imgSrc) return;
        const img = new Image();
        img.src = imgSrc;
      });

      // Add the SKU to preloaded ones
      this.preloadedSkus[item.sku] = true;
    },
  },
};
</script>

<template>
  <div class="app__color__picker js__filters-wrapper">
    <div
      v-for="option in optionsList"
      :key="option.id"
    >
      <div class="app__color__picker__title">
        <g-copy
          type="small"
          v-html="getColorPickerTitle(option)"
        />
      </div>
      <ul class="app__color__picker__list">
        <li
          v-for="item in getImages(option.id)"
          :key="item.sku"
          class="app__color__picker__item"
          data-testid="color-picker-item"
        >
          <a
            class="app__color__picker__link js__filter-option"
            :class="calculateLinkClass(item)"
            :href="getUrl(item?.url)"
            :data-testid="`color-picker-${item?.sku}`"
            @click.prevent="changeColor(item)"
            @mouseover="preloadImages(item)"
          >
            <img
              :src="item.image"
              :alt="getImageAltText(item)"
            >
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app__color__picker {
  margin: 1rem 0;

  &__title {
    :deep(.g__copy) {
      margin-bottom: 0.5rem;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-flow: row wrap;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 0.6em 0.6em;

    @include productBreakpoint('desktop-s') {
      max-width: 570px;
    }
  }

  &__item {
    text-transform: uppercase;
    display: block;
    font-size: 0.9em;
    margin: 0;

    img {
      width: 60px;
      height: 60px;
      overflow: hidden;
      opacity: 0.3;
      vertical-align: middle;

      @include breakpoint(sm) {
        width: 80px;
        height: 80px;
      }
    }
  }

  &__link {
    display: block;
    padding: 0;
    border: 2px solid $color-gray-80;
    transition: background-color 0.5s, border-color 0.5s, color 0.5s;

    &:hover {
      border-color: $color-black;
    }

    &__active,
    &__selected {

      img {
        transition: opacity 0.5s;
        opacity: 1;
      }
    }

    &__selected {
      border-color: $color-black;
    }
  }
}
</style>
